import React, { useState, useEffect, useMemo } from "react";
import { inspectionsAPI } from "../../../../src/services/api";
import { useProjectContext } from "../../../hooks/useProjectContext";

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm text-gray-700">{label}</label>
    {children}
  </div>
);

const InspectionForm = ({ value, onChange, onSubmit, onCancel, creating }) => {
  const { activeProject } = useProjectContext();
  const projectId = activeProject?.id;
  const v = value || {};

  const [wxLoading, setWxLoading] = useState(false);
  const [unitSystem, setUnitSystem] = useState("imperial");

  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  // Run auto weather fill once when a new inspection is opened
  const [autoWeatherDone, setAutoWeatherDone] = useState(false);

  // 🔹 Multi-select UI state for inspection types
  const [typesExpanded, setTypesExpanded] = useState(true);
  const [typeSearch, setTypeSearch] = useState("");

  const selectedTypeIds = Array.isArray(v.inspection_type_ids)
    ? v.inspection_type_ids
    : [];

  const selectedTypes = useMemo(
    () => (inspectionTypes || []).filter((t) => selectedTypeIds.includes(t.id)),
    [inspectionTypes, selectedTypeIds],
  );

  const filteredTypes = useMemo(() => {
    const q = typeSearch.trim().toLowerCase();
    const list = inspectionTypes || [];
    if (!q) return list;
    return list.filter((t) => {
      const name = (t.name || "").toLowerCase();
      const desc = (t.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [inspectionTypes, typeSearch]);

  const toggleTypeId = (id) => {
    const prevIds = Array.isArray(v.inspection_type_ids)
      ? v.inspection_type_ids
      : [];
    const exists = prevIds.includes(id);
    const nextIds = exists ? prevIds.filter((x) => x !== id) : [...prevIds, id];

    onChange("inspection_type_ids", nextIds);

    // Keep legacy string field in sync (comma-separated names)
    const allTypes = inspectionTypes || [];
    const nextSelected = allTypes.filter((t) => nextIds.includes(t.id));
    const label = nextSelected.map((t) => t.name).join(", ");
    onChange("inspection_type", label);
  };

  const autoFillWeather = async () => {
    if (!projectId) return alert("No project selected");
    if (!v.location) return alert("Please enter location first");
    if (!v.inspection_date) return alert("Please choose inspection date first");

    setWxLoading(true);
    try {
      const res = await inspectionsAPI.autofillWeather(projectId, {
        location: v.location,
        inspection_date: v.inspection_date,
        units: unitSystem,
      });

      const data = res.data || {};
      Object.entries(data).forEach(([k, val]) => onChange(k, val ?? ""));
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to auto-fill weather");
    } finally {
      setWxLoading(false);
    }
  };

  // Map legacy inspection_type → inspection_type_ids on first load
  useEffect(() => {
    if (!projectId) return;
    if (!inspectionTypes.length) return;
    if (!v) return;

    const hasIds =
      Array.isArray(v.inspection_type_ids) && v.inspection_type_ids.length > 0;
    if (hasIds) return;

    if (v.inspection_type) {
      const match = inspectionTypes.find(
        (t) => (t.name || "").trim() === v.inspection_type.trim(),
      );
      if (match) {
        onChange("inspection_type_ids", [match.id]);
      }
    }
  }, [
    projectId,
    inspectionTypes,
    v,
    v?.inspection_type,
    v?.inspection_type_ids,
    onChange,
  ]);

  useEffect(() => {
    if (!projectId) return;

    setLoadingTypes(true);
    inspectionsAPI
      .listProjectInspectionTypes(projectId)
      .then((res) => setInspectionTypes(res.data || []))
      .catch((err) => {
        console.error("Failed to load inspection types", err);
        setInspectionTypes([]);
      })
      .finally(() => setLoadingTypes(false));
  }, [projectId]);

  // Auto-prefill weather once if form has date+location and no weather yet
  useEffect(() => {
    // Don't run if already done
    if (autoWeatherDone) return;
    // Need project, date and location
    if (!projectId) return;
    if (!v.location || !v.inspection_date) return;

    // If there is already weather data (edit existing inspection), don't override
    const hasWeatherData =
      !!v.weather_conditions ||
      !!v.temperature_high ||
      !!v.temperature_low ||
      !!v.wind ||
      !!v.precipitation_type ||
      !!v.precipitation_amount;

    if (hasWeatherData) return;

    const run = async () => {
      setWxLoading(true);
      try {
        const res = await inspectionsAPI.autofillWeather(projectId, {
          location: v.location,
          inspection_date: v.inspection_date,
          units: unitSystem,
        });

        const data = res.data || {};
        Object.entries(data).forEach(([k, val]) => onChange(k, val ?? ""));
      } catch (e) {
        // Silent failure; user can still click the button manually
        console.error("Auto weather prefill failed", e);
      } finally {
        setWxLoading(false);
        setAutoWeatherDone(true);
      }
    };

    run();
  }, [
    projectId,
    v.location,
    v.inspection_date,
    v.weather_conditions,
    v.temperature_high,
    v.temperature_low,
    v.wind,
    v.precipitation_type,
    v.precipitation_amount,
    unitSystem,
    autoWeatherDone,
    onChange,
  ]);

  const hasProjectCoords =
    activeProject &&
    activeProject.latitude !== null &&
    activeProject.latitude !== undefined &&
    activeProject.longitude !== null &&
    activeProject.longitude !== undefined;

  useEffect(() => {
    // Need a project and coords
    if (!projectId) return;
    if (!hasProjectCoords) return;

    // If the user (or parent) already set a location, don't overwrite
    if (v.location && v.location.toString().trim() !== "") return;

    // At this point: form has no location, but project has coords → set it
    onChange(
      "location",
      `${activeProject.latitude},${activeProject.longitude}`,
    );
  }, [
    projectId,
    hasProjectCoords,
    activeProject?.latitude,
    activeProject?.longitude,
    v.location,
    onChange,
  ]);

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white rounded-lg shadow p-6 space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Inspection Date">
          <input
            type="date"
            value={v.inspection_date || ""}
            onChange={(e) => onChange("inspection_date", e.target.value)}
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Inspection Types">
          <div className="mt-1">
            {/* Selected chips + expand/collapse control */}
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex flex-wrap gap-1">
                {selectedTypes.length === 0 && !loadingTypes && (
                  <span className="text-xs text-gray-400">
                    No inspection types selected
                  </span>
                )}
                {selectedTypes.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleTypeId(t.id)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-gray-300 bg-gray-50 hover:bg-gray-100"
                  >
                    <span>{t.name}</span>
                    <span className="text-[10px] text-gray-500">×</span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setTypesExpanded((v) => !v)}
                className="text-xs text-gray-500 hover:text-gray-800"
                disabled={loadingTypes}
              >
                {typesExpanded ? "× Hide list" : "Show list"}
              </button>
            </div>

            {typesExpanded && (
              <div className="border rounded-md">
                {/* Search box */}
                <div className="px-2 py-1 border-b flex items-center gap-1 bg-gray-50">
                  <input
                    type="text"
                    value={typeSearch}
                    onChange={(e) => setTypeSearch(e.target.value)}
                    disabled={loadingTypes}
                    placeholder="Search inspection type…"
                    className="flex-1 text-xs bg-transparent outline-none"
                  />
                </div>

                {/* List with checkboxes */}
                <div className="max-h-56 overflow-y-auto text-sm">
                  {loadingTypes && (
                    <div className="px-3 py-2 text-xs text-gray-500">
                      Loading inspection types…
                    </div>
                  )}

                  {!loadingTypes && (inspectionTypes || []).length === 0 && (
                    <div className="px-3 py-2 text-xs text-gray-400">
                      No inspection templates defined for this project yet.
                    </div>
                  )}

                  {!loadingTypes &&
                    (inspectionTypes || []).length > 0 &&
                    filteredTypes.length === 0 && (
                      <div className="px-3 py-2 text-xs text-gray-400">
                        No types match "{typeSearch}".
                      </div>
                    )}

                  {!loadingTypes &&
                    filteredTypes.map((t) => {
                      const checked = selectedTypeIds.includes(t.id);
                      return (
                        <label
                          key={t.id}
                          className="flex items-start gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 border-gray-100"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={checked}
                            onChange={() => toggleTypeId(t.id)}
                          />
                          <div>
                            <div className="font-medium text-gray-800 text-sm">
                              {t.name}
                            </div>
                            {t.description && (
                              <div className="text-xs text-gray-500">
                                {t.description}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </Field>

        <Field label="Location">
          <input
            value={v.location || ""}
            onChange={(e) => onChange("location", e.target.value)}
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
            placeholder={
              hasProjectCoords
                ? `${activeProject.latitude},${activeProject.longitude}`
                : ""
            }
          />
          {hasProjectCoords && (
            <p className="mt-1 text-xs text-gray-500">
              Default from project coordinates: {activeProject.latitude},{" "}
              {activeProject.longitude}
            </p>
          )}
        </Field>

        <Field label="Site location">
          <input
            value={v.site_location || ""}
            onChange={(e) => onChange("site_location", e.target.value)}
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
            placeholder="e.g. Level 2 – Electrical Room, Area B"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Units:">
          <div className="mt-1 inline-flex rounded-md border bg-gray-50 overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setUnitSystem("metric")}
              className={
                "px-3 py-1.5 " +
                (unitSystem === "metric"
                  ? "bg-gray-900 text-white"
                  : "text-gray-700 hover:bg-gray-100")
              }
            >
              Metric (°C, m/s)
            </button>
            <button
              type="button"
              onClick={() => setUnitSystem("imperial")}
              className={
                "px-3 py-1.5 border-l " +
                (unitSystem === "imperial"
                  ? "bg-gray-900 text-white"
                  : "text-gray-700 hover:bg-gray-100")
              }
            >
              US (°F, mph)
            </button>
          </div>
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Precipitation Type">
          <input
            value={v.precipitation_type || ""}
            onChange={(e) => onChange("precipitation_type", e.target.value)}
            placeholder="e.g., Rain, Snow"
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Precipitation Amount">
          <input
            value={v.precipitation_amount || ""}
            onChange={(e) => onChange("precipitation_amount", e.target.value)}
            placeholder={
              unitSystem === "metric" ? "e.g., 2.0 mm/hr" : "e.g., 0.10 in/hr"
            }
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Wind">
          <input
            value={v.wind || ""}
            onChange={(e) => onChange("wind", e.target.value)}
            placeholder={
              unitSystem === "metric" ? "e.g., 5.0 m/s NW" : "e.g., 10.0 mph NW"
            }
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Temperature High">
          <input
            value={v.temperature_high || ""}
            onChange={(e) => onChange("temperature_high", e.target.value)}
            placeholder={
              unitSystem === "metric" ? "e.g., 12.0 °C" : "e.g., 54.0 °F"
            }
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Temperature Low">
          <input
            value={v.temperature_low || ""}
            onChange={(e) => onChange("temperature_low", e.target.value)}
            placeholder={
              unitSystem === "metric" ? "e.g., 3.0 °C" : "e.g., 38.0 °F"
            }
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Weather Conditions">
          <input
            value={v.weather_conditions || ""}
            onChange={(e) => onChange("weather_conditions", e.target.value)}
            placeholder="e.g., Cloudy"
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          />
        </Field>
      </div>

      <div>
        <label className="text-sm text-gray-700">Notes</label>
        <textarea
          value={v.notes || ""}
          onChange={(e) => onChange("notes", e.target.value)}
          rows={4}
          className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
        />
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={autoFillWeather}
          disabled={wxLoading || creating}
          className="px-4 py-2 rounded-md border text-sm"
        >
          {wxLoading ? "Auto-filling…" : "Auto-fill Weather"}
        </button>

        <button
          type="submit"
          disabled={creating}
          className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm"
        >
          {creating ? "Saving…" : "Create"}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-md text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default React.memo(InspectionForm);
